import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Flex,
  Text,
  Button,
  IconButton,
  Icon,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  VStack,
  HStack,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  useDisclosure,
  useToast,
  Badge,
  Container,
  Divider,
  useBreakpointValue,
  InputGroup,
  Input,
  InputLeftElement,
  InputRightElement,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
} from '@chakra-ui/react';
import {
  HamburgerIcon,
  ChevronDownIcon,
  BellIcon,
  SearchIcon,
} from '@chakra-ui/icons';
import { FaUser, FaSignOutAlt, FaCog, FaHome, FaGopuram, FaFacebook, FaYoutube, FaInstagram, FaUserCircle, FaCheckDouble, FaCheck, FaTrash, FaPhoneAlt, FaCalendarAlt, FaImages, FaMusic, FaUtensils, FaHeart, FaComments, FaBookOpen, FaStore, FaVideo, FaHandsHelping, FaHistory, FaAward, FaBell } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import { useNotifications } from '../../context/NotificationContext';

import { useTranslation } from '../../hooks/useTranslation';
import { useSiteConfig } from '../../context/SiteConfigContext';
import { templeAssets } from '../../config/assets';
import CircularLogo from '../common/CircularLogo';
import LanguageSelector from '../common/LanguageSelector';
import AdminNotificationButton from '../admin/AdminNotificationButton';
import { formatDate, formatTime } from '../../utils/themeUtils';

const MotionBox = motion(Box);

const Header = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isComingSoonOpen, onOpen: onComingSoonOpen, onClose: onComingSoonClose } = useDisclosure();
  const { user, logout } = useAuth();
  const { config } = useSiteConfig();
  const { notifications, markAsRead, markAllAsRead, clearAllNotifications, deleteNotification } = useNotifications();
  const { t, language } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const isMobile = useBreakpointValue({ base: true, md: false });

  // Exact colors from reference site
  const darkRed = '#620404'; // Top bar color
  const gold = '#ecbd00'; // Yellow/Gold color
  const white = '#ffffff';
  const textColor = '#1f2937';
  const [currentDateTime, setCurrentDateTime] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Values based on color mode
  const headerBg = gold; // Satvic surface color
  const headerText = darkRed;
  const iconColor = darkRed;
  const topBarBg = darkRed; // Satvic bg
  const drawerBg = 'white';
  const drawerBodyColor = textColor;

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setShowSearch(false);
      setSearchQuery('');
    }
  };

  useEffect(() => {
    const updateDateTime = () => {
      const now = new Date();
      const locale = language === 'te' ? 'te-IN' : 'en-IN';
      const dateStr = formatDate(now, {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      }, locale);
      const timeStr = formatTime(now, {}, locale);

      setCurrentDateTime(`${dateStr} - ${timeStr}`);
    };
    updateDateTime();
    const interval = setInterval(updateDateTime, 60000); // Update every minute
    return () => clearInterval(interval);
  }, [language]);



  const handleLogout = async () => {
    try {
      await logout();
      toast({
        title: t('common.loggedOutSuccessfully') || 'Logged out successfully',
        description: `Jai ${templeAssets.templeName}`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      navigate('/');
    } catch (error) {
      toast({
        title: 'Logout failed',
        description: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const isActive = (path) => location.pathname === path;

  const row1Items = [
    { name: t('header.about'), path: '/temple-history', isComingSoon: true, icon: FaHistory },
    { name: t('header.sevasAndDarshanam'), path: '/Abhishekam', icon: FaGopuram },
    { name: t('header.bhajans') || 'Bhajans', path: '/bhajans', isComingSoon: true, icon: FaMusic },
    { name: t('common.contact') || 'Contact', path: '/contact', icon: FaPhoneAlt },
    { name: t('header.prasadamDetails') || 'Prasadam Details', path: '/prasadam-details', icon: FaUtensils },
    { name: t('header.donate'), path: '/donate', icon: FaHeart },
    { name: t('header.feedback') || 'Feedback', path: '/feedback', icon: FaComments },
  ];

  const row2Items = [
    { name: t('common.festivals') || 'Festivals', path: '/festivals', icon: FaCalendarAlt },
    { name: t('header.gallery') || 'Gallery', path: '/gallery', icon: FaImages },
    { name: t('header.panchangam') || 'Panchangam', path: '/panchangam', icon: FaBookOpen },
    { name: t('header.templeShop') || 'Temple Shop', path: '/shop', isComingSoon: true, icon: FaStore },
    { name: t('header.virtualDarshan') || 'Virtual Darshan', path: '/virtual-darshan', icon: FaVideo },
    { name: t('header.volunteer') || 'Volunteer', path: '/volunteer', isComingSoon: true, icon: FaHandsHelping },
    { name: language === 'te' ? 'దాతల గోడ' : 'Donor Wall', path: '/donor-wall', icon: FaAward },
  ];

  const navItems = [...row1Items, ...row2Items];

  const unreadNotifications = notifications.filter(n => !n.isRead).length;

  return (
    <Box>
      {/* Top Dark Bar - Satvic Themes */}
      <Box bg={topBarBg} color={white} py={1} fontSize="xs">
        <Container maxW="100%" pl={{ base: 2, md: 8 }} pr={{ base: 2, md: 4 }}>
          <Flex direction={{ base: 'row', md: 'row' }} justify="space-between" align="center" gap={{ base: 2, md: 0 }}>
            <HStack spacing={{ base: 2, md: 4 }} pl={{ base: 0, lg: "160px" }} justify={{ base: 'flex-start', md: 'flex-start' }} w={{ base: 'auto', md: 'auto' }} flexWrap="wrap">
              <Text fontFamily="Roboto, sans-serif" fontWeight="500" fontSize={{ base: '10px', sm: 'xs' }}>
                {currentDateTime}
              </Text>
              <Badge colorScheme="yellow" bg={gold} color={darkRed} variant="solid" fontSize="10px" px={2} py={0.5} borderRadius="full" fontWeight="700">
                {language === 'te' ? 'ప్రత్యేక రోజులు: శుక్రవారం & ఆదివారం' : 'Special Days: Friday & Sunday'}
              </Badge>
              <HStack spacing={1}>
                <IconButton
                  as="a"
                  href={templeAssets.socialMedia.facebook}
                  target="_blank"
                  icon={<FaFacebook />}
                  variant="ghost"
                  size="xs"
                  color={white}
                  _hover={{ bg: 'rgba(255,255,255,0.1)', transform: 'scale(1.1)' }}
                  aria-label="Facebook"
                  cursor="pointer"
                />
                <IconButton
                  as="a"
                  href={templeAssets.socialMedia.instagram}
                  target="_blank"
                  icon={<FaInstagram />}
                  variant="ghost"
                  size="xs"
                  color={white}
                  _hover={{ bg: 'rgba(255,255,255,0.1)', transform: 'scale(1.1)' }}
                  aria-label="Instagram"
                  cursor="pointer"
                />
                <IconButton
                  as="a"
                  href={templeAssets.socialMedia.youtube}
                  target="_blank"
                  icon={<FaYoutube />}
                  variant="ghost"
                  size="xs"
                  color={white}
                  _hover={{ bg: 'rgba(255,255,255,0.1)', transform: 'scale(1.1)' }}
                  aria-label="YouTube"
                  cursor="pointer"
                />
              </HStack>
            </HStack>

            <HStack
              spacing={0}
              divider={<Divider orientation="vertical" h="20px" borderColor="rgba(255,255,255,0.3)" />}
              justify={{ base: 'center', md: 'flex-end' }}
              w={{ base: 'full', md: 'auto' }}
              pb={{ base: 1, md: 0 }}
            >
              <HStack
                as={Link}
                to="/live-tv"
                px={{ base: 1, sm: 4 }}
                spacing={{ base: 1, sm: 2 }}
                display={{ base: 'none', sm: 'flex' }}
                cursor="pointer"
                _hover={{ opacity: 0.8, transform: 'scale(1.05)' }}
                transition="all 0.2s"
              >
                <Box position="relative">
                  <Box border="1px solid white" p={0.5} borderRadius="sm">
                    <FaHome size="10px" />
                  </Box>
                  {config.isLiveTvEnabled && (
                    <Box
                      position="absolute"
                      top="-1"
                      right="-1"
                      w="6px"
                      h="6px"
                      bg="red.500"
                      borderRadius="full"
                      as={motion.div}
                      animate={{ opacity: [1, 0, 1] }}
                      transition={{ duration: 1.5, repeat: Infinity }}
                    />
                  )}
                </Box>
                <Text fontFamily="Roboto, sans-serif" fontWeight="600" fontSize="10px">
                  {templeAssets.templeNameShort} TV
                </Text>
              </HStack>

              <Box px={{ base: 2, md: 4 }}>
                <LanguageSelector variant="compact" />
              </Box>

              {user && ['admin', 'sub-admin'].includes(user.role?.toLowerCase()) && (
                <Box px={{ base: 1, md: 2 }}>
                  <AdminNotificationButton variant="header" forceShow={true} />
                </Box>
              )}

              {user ? (
                <Menu>
                  <MenuButton
                    as={Button}
                    variant="ghost"
                    size="xs"
                    color={white}
                    px={{ base: 2, md: 4 }}
                    _hover={{ bg: 'rgba(255,255,255,0.1)' }}
                    leftIcon={<FaUserCircle />}
                    rightIcon={<ChevronDownIcon />}
                    fontFamily="Roboto, sans-serif"
                    fontWeight="600"
                    cursor="pointer"
                  >
                    {isMobile ? user.name.split(' ')[0].toUpperCase() : user.name.toUpperCase()}
                  </MenuButton>
                  <MenuList color={textColor} zIndex={10}>
                    <Box px={4} py={3} borderBottom="1px solid" borderColor="gray.200" mb={1}>
                      <Text fontWeight="bold" fontSize="md">{user.name}</Text>
                      <Text fontSize="sm" color="gray.600">{user.email}</Text>
                      {user.phone && <Text fontSize="sm" color="gray.600">{user.phone}</Text>}
                    </Box>
                    <MenuItem icon={<FaUserCircle />} onClick={() => navigate('/profile')}>
                      {t('common.profile') || 'My Profile'}
                    </MenuItem>
                    <MenuItem icon={<FaUser />} onClick={() => navigate('/dashboard')}>
                      {t('common.dashboard')}
                    </MenuItem>
                    {['admin', 'sub-admin'].includes(user.role?.toLowerCase()) && (
                      <>
                        <MenuItem icon={<FaCog />} onClick={() => navigate(user.role?.toLowerCase() === 'admin' ? '/admin' : '/sub-admin')}>
                          {user.role?.toLowerCase() === 'admin' ? t('header.adminPanel') : t('header.subAdminPanel')}
                        </MenuItem>
                        <MenuItem icon={<FaBell color="#e53e3e" />} onClick={() => navigate(user.role?.toLowerCase() === 'admin' ? '/admin?openNotifs=true' : '/sub-admin?openNotifs=true')}>
                          Admin Notifications
                        </MenuItem>
                      </>
                    )}
                    <MenuDivider />
                    <MenuItem icon={<FaSignOutAlt />} onClick={handleLogout}>
                      {t('common.logout')}
                    </MenuItem>
                  </MenuList>
                </Menu>
              ) : (
                <Button
                  variant="ghost"
                  size="xs"
                  color={white}
                  px={{ base: 2, md: 4 }}
                  _hover={{ bg: 'rgba(255,255,255,0.1)' }}
                  leftIcon={<FaUserCircle />}
                  onClick={() => navigate('/login')}
                  fontFamily="Roboto, sans-serif"
                  fontWeight="600"
                  cursor="pointer"
                >
                  {t('common.login')}
                </Button>
              )}
            </HStack>
          </Flex>
        </Container>
      </Box>

      {/* Main Header Area - Satvic Themes */}
      <Box bg={headerBg} borderBottom={`2px solid ${darkRed}`}>
        <Container maxW="100%" px={{ base: 2, md: 8 }}>
          <Flex justify="space-between" align="center" py={1.5}>
            {/* Left: Logo and Title */}
            <HStack spacing={{ base: 1, sm: 2, md: 4 }} align="center" flex="1">
              <Box mt={{ base: "-5px", md: "-15px" }} mb="-5px" cursor="pointer" onClick={() => navigate('/')} flexShrink={0}>
                <CircularLogo size={isMobile ? 60 : 100} />
              </Box>
              <VStack align="flex-start" spacing={0} py={2}>
                <Text
                  fontSize={{ base: '14px', sm: '18px', md: '26px' }}
                  fontWeight="900"
                  color={darkRed}
                  fontFamily="'Poppins', sans-serif"
                  lineHeight="1.1"
                  letterSpacing="-0.3px"
                  whiteSpace="normal"
                >
                  {templeAssets.templeName.split(' ').slice(0, 3).join(' ')}
                </Text>
                <Text
                  fontSize={{ base: '14px', sm: '18px', md: '26px' }}
                  fontWeight="900"
                  color={darkRed}
                  fontFamily="'Poppins', sans-serif"
                  lineHeight="1.1"
                  letterSpacing="-0.3px"
                  whiteSpace="normal"
                >
                  {templeAssets.templeName.split(' ').slice(3).join(' ')}
                </Text>
              </VStack>
            </HStack>

            {/* Right Side */}
            <Flex align="center">
              {/* Desktop Nav and Actions */}
              <VStack display={{ base: 'none', lg: 'flex' }} align="flex-end" spacing={1.5}>
                {/* Top row: Other links and icons */}
                <HStack spacing={6} pr={2}>
                  <HStack spacing={3}>
                    
                    {/* Notification Bell */}
                    {user && (
                      <Menu>
                        <MenuButton
                          as={IconButton}
                          aria-label="Notifications"
                          icon={
                            <Box position="relative">
                              <BellIcon w={5} h={5} />
                              {unreadNotifications > 0 && (
                                <Badge
                                  position="absolute"
                                  top="-1"
                                  right="-1"
                                  colorScheme="red"
                                  borderRadius="full"
                                  fontSize="xs"
                                  minW="16px"
                                  h="16px"
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="center"
                                  boxShadow="0 0 0 2px white"
                                >
                                  {unreadNotifications}
                                </Badge>
                              )}
                            </Box>
                          }
                          variant="ghost"
                          color={iconColor}
                          size="xs"
                          _hover={{ bg: 'rgba(98, 4, 4, 0.1)', transform: 'scale(1.1)' }}
                          transition="all 0.2s"
                          cursor="pointer"
                        />
                        <MenuList maxH="400px" overflowY="auto" minW="350px" p={0} zIndex={20} border="1px solid" borderColor="gray.100" boxShadow="xl" borderRadius="lg">
                          <Box p={3} bg={darkRed} color="white" borderTopRadius="md">
                            <Flex justify="space-between" align="center">
                              <HStack spacing={2}>
                                <Text fontWeight="600" fontSize="sm" fontFamily="Roboto, sans-serif">
                                  {t('header.notifications')}
                                </Text>
                                {unreadNotifications > 0 && (
                                  <Badge colorScheme="yellow" color={darkRed} bg={gold} borderRadius="full" fontSize="xs">
                                    {unreadNotifications} {t('header.new')}
                                  </Badge>
                                )}
                              </HStack>
                              <HStack spacing={3}>
                                {unreadNotifications > 0 && (
                                  <IconButton
                                    icon={<FaCheckDouble size={12} />}
                                    aria-label="Mark all read"
                                    size="xs"
                                    variant="ghost"
                                    color="white"
                                    _hover={{ color: gold, bg: 'rgba(255, 255, 255, 0.1)' }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      markAllAsRead();
                                    }}
                                    title="Mark all as read"
                                  />
                                )}
                                {notifications.length > 0 && (
                                  <IconButton
                                    icon={<FaTrash size={12} />}
                                    aria-label="Clear all"
                                    size="xs"
                                    variant="ghost"
                                    color="white"
                                    _hover={{ color: 'red.300', bg: 'rgba(255, 255, 255, 0.1)' }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      clearAllNotifications();
                                    }}
                                    title="Clear all"
                                  />
                                )}
                              </HStack>
                            </Flex>
                          </Box>
                          {notifications.length === 0 ? (
                            <Box p={6} textAlign="center">
                              <Text color="gray.500" fontSize="sm" fontFamily="Roboto, sans-serif">
                                {t('notifications.noNotifications')}
                              </Text>
                            </Box>
                          ) : (
                            <VStack spacing={0} align="stretch" maxH="300px" overflowY="auto">
                              {notifications.slice(0, 5).map((notification) => (
                                <Box
                                  key={notification.id}
                                  py={3}
                                  px={4}
                                  bg={!notification.isRead ? '#fffbeb' : 'transparent'}
                                  borderLeft={!notification.isRead ? '3px solid' : '3px solid transparent'}
                                  borderLeftColor={gold}
                                  _hover={{ bg: '#f9fafb' }}
                                  transition="all 0.2s"
                                  borderBottom="1px solid"
                                  borderBottomColor="gray.100"
                                  position="relative"
                                  role="group"
                                  cursor="pointer"
                                  onClick={async () => {
                                    if (!notification.isRead) {
                                      await markAsRead(notification.id);
                                    }
                                    if (notification.actionUrl) {
                                      navigate(notification.actionUrl);
                                    } else {
                                      navigate('/notifications');
                                    }
                                  }}
                                >
                                  <Flex justify="space-between" align="start" gap={2}>
                                    <VStack align="start" spacing={1} flex="1">
                                      <HStack spacing={2} w="100%" justify="space-between">
                                        <Text fontWeight={!notification.isRead ? '600' : 'normal'} fontSize="sm" color={textColor} noOfLines={1} pr={4}>
                                          {notification.title}
                                        </Text>
                                        {!notification.isRead && (
                                          <Badge colorScheme="red" borderRadius="full" w="6px" h="6px" />
                                        )}
                                      </HStack>
                                      <Text fontSize="xs" color="gray.600" noOfLines={2}>
                                        {notification.message}
                                      </Text>
                                      <Text fontSize="10px" color="gray.400">
                                        {new Date(notification.createdAt).toLocaleDateString()}
                                      </Text>
                                    </VStack>
                                    <HStack 
                                      spacing={1} 
                                      opacity={0} 
                                      _groupHover={{ opacity: 1 }} 
                                      transition="opacity 0.2s"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      {!notification.isRead && (
                                        <IconButton
                                          icon={<FaCheck size={10} />}
                                          aria-label="Mark as read"
                                          size="xs"
                                          variant="ghost"
                                          colorScheme="blue"
                                          onClick={() => markAsRead(notification.id)}
                                        />
                                      )}
                                      <IconButton
                                        icon={<FaTrash size={10} />}
                                        aria-label="Delete"
                                        size="xs"
                                        variant="ghost"
                                        colorScheme="red"
                                        onClick={() => deleteNotification(notification.id)}
                                      />
                                    </HStack>
                                  </Flex>
                                </Box>
                              ))}
                            </VStack>
                          )}
                          <Divider />
                          <MenuItem onClick={() => navigate('/notifications')} fontFamily="Roboto, sans-serif" _hover={{ bg: 'gray.50' }}>
                            <Text fontSize="sm" fontWeight="600" textAlign="center" w="100%" color={darkRed}>
                              {t('notifications.viewAll')}
                            </Text>
                          </MenuItem>
                        </MenuList>
                      </Menu>
                    )}

                    <IconButton
                      icon={<SearchIcon w={4} h={4} />}
                      variant="ghost"
                      color={iconColor}
                      size="xs"
                      _hover={{ bg: 'rgba(98, 4, 4, 0.1)', transform: 'scale(1.1)' }}
                      transition="all 0.2s"
                      aria-label="Search"
                      onClick={() => setShowSearch(!showSearch)}
                      cursor="pointer"
                    />
                  </HStack>
                </HStack>

                {/* Bottom row: Main Navigation */}
                {/* Row 1 Navigation */}
                <HStack
                  spacing={0}
                  divider={<Divider orientation="vertical" borderColor={darkRed} h="22px" opacity={0.2} />}
                  bg={`${gold}10`}
                  borderRadius="full"
                  px={2}
                  border={`1px solid ${darkRed}20`}
                >
                  {row1Items.map((item) => (
                    item.onClick ? (
                      <Button
                        key={`row1-${item.name}`}
                        onClick={item.onClick}
                        variant="ghost"
                        size="sm"
                        color={darkRed}
                        fontWeight="800"
                        px={4}
                        height="34px"
                        fontSize="13px"
                        borderRadius="0"
                        _hover={{
                          bg: 'rgba(98, 4, 4, 0.08)',
                          color: '#000',
                          transform: 'translateY(-1px)'
                        }}
                        transition="all 0.2s"
                        fontFamily="'Poppins', sans-serif"
                        cursor="pointer"
                        textTransform="uppercase"
                        letterSpacing="0.5px"
                      >
                        {item.name}
                      </Button>
                    ) : item.isComingSoon ? (
                      <Button
                        key={`row1-${item.name}-${item.path}`}
                        onClick={(e) => { e.preventDefault(); onComingSoonOpen(); }}
                        variant="ghost"
                        size="sm"
                        color={isActive(item.path) ? darkRed : darkRed}
                        fontWeight="800"
                        px={4}
                        height="34px"
                        fontSize="13px"
                        borderRadius="0"
                        _hover={{
                          bg: 'rgba(98, 4, 4, 0.08)',
                          color: '#000',
                          transform: 'translateY(-1px)'
                        }}
                        transition="all 0.2s"
                        fontFamily="'Poppins', sans-serif"
                        cursor="pointer"
                        textTransform="uppercase"
                        letterSpacing="0.5px"
                      >
                        {item.name}
                      </Button>
                    ) : (
                      <Link key={`row1-${item.name}-${item.path}`} to={item.path}>
                        <Button
                          variant="ghost"
                          size="sm"
                          color={isActive(item.path) ? darkRed : darkRed}
                          fontWeight="800"
                          px={4}
                          height="34px"
                          fontSize="13px"
                          borderRadius="0"
                          _hover={{
                            bg: 'rgba(98, 4, 4, 0.08)',
                            color: '#000',
                            transform: 'translateY(-1px)'
                          }}
                          transition="all 0.2s"
                          fontFamily="'Poppins', sans-serif"
                          cursor="pointer"
                          textTransform="uppercase"
                          letterSpacing="0.5px"
                        >
                          {item.name}
                        </Button>
                      </Link>
                    )
                  ))}
                </HStack>

                {/* Row 2 Navigation */}
                <HStack
                  spacing={0}
                  divider={<Divider orientation="vertical" borderColor={darkRed} h="22px" opacity={0.2} />}
                  bg={`${gold}10`}
                  borderRadius="full"
                  px={2}
                  border={`1px solid ${darkRed}20`}
                >
                  {row2Items.map((item) => (
                    item.isComingSoon ? (
                      <Button
                        key={`row2-${item.name}-${item.path}`}
                        onClick={(e) => { e.preventDefault(); onComingSoonOpen(); }}
                        variant="ghost"
                        size="sm"
                        color={isActive(item.path) ? darkRed : darkRed}
                        fontWeight="800"
                        px={4}
                        height="34px"
                        fontSize="13px"
                        borderRadius="0"
                        _hover={{
                          bg: 'rgba(98, 4, 4, 0.08)',
                          color: '#000',
                          transform: 'translateY(-1px)'
                        }}
                        transition="all 0.2s"
                        fontFamily="'Poppins', sans-serif"
                        cursor="pointer"
                        textTransform="uppercase"
                        letterSpacing="0.5px"
                      >
                        {item.name}
                      </Button>
                    ) : (
                      <Link key={`row2-${item.name}-${item.path}`} to={item.path}>
                        <Button
                          variant="ghost"
                          size="sm"
                          color={isActive(item.path) ? darkRed : darkRed}
                          fontWeight="800"
                          px={4}
                          height="34px"
                          fontSize="13px"
                          borderRadius="0"
                          _hover={{
                            bg: 'rgba(98, 4, 4, 0.08)',
                            color: '#000',
                            transform: 'translateY(-1px)'
                          }}
                          transition="all 0.2s"
                          fontFamily="'Poppins', sans-serif"
                          cursor="pointer"
                          textTransform="uppercase"
                          letterSpacing="0.5px"
                        >
                          {item.name}
                        </Button>
                      </Link>
                    )
                  ))}
                </HStack>
              </VStack>

              {/* Mobile Actions and Hamburger */}
              <HStack display={{ base: 'flex', lg: 'none' }} spacing={2}>
                {/* Mobile Notification Bell */}
                {user && (
                  <Menu>
                    <MenuButton
                      as={IconButton}
                      aria-label="Notifications"
                      icon={
                        <Box position="relative">
                          <BellIcon w={5} h={5} />
                          {unreadNotifications > 0 && (
                            <Badge
                              position="absolute"
                              top="-1"
                              right="-1"
                              colorScheme="red"
                              borderRadius="full"
                              fontSize="xs"
                              minW="14px"
                              h="14px"
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                            >
                              {unreadNotifications}
                            </Badge>
                          )}
                        </Box>
                      }
                      variant="ghost"
                      color={iconColor}
                      size="sm"
                      cursor="pointer"
                    />
                    <MenuList maxH="350px" overflowY="auto" minW="280px" zIndex={9999}>
                      {notifications.length === 0 ? (
                        <Box p={4} textAlign="center">
                          <Text color="gray.500" fontSize="sm">{t('notifications.noNotifications') || 'No notifications found'}</Text>
                        </Box>
                      ) : (
                        <>
                          {notifications.slice(0, 5).map((notification) => (
                            <MenuItem key={notification.id} onClick={() => {
                              navigate(notification.actionUrl || '/notifications');
                              onClose();
                            }}>
                              <VStack align="start" spacing={0} w="full">
                                <Text fontWeight="600" fontSize="sm" noOfLines={1}>{notification.title}</Text>
                                <Text fontSize="xs" color="gray.600" noOfLines={1}>{notification.message}</Text>
                              </VStack>
                            </MenuItem>
                          ))}
                          <MenuDivider />
                          <MenuItem onClick={() => { navigate('/notifications'); onClose(); }} justifyContent="center" fontWeight="bold" color="red.800" fontSize="sm">
                            {t('notifications.viewAll') || 'View All Notifications'}
                          </MenuItem>
                        </>
                      )}
                    </MenuList>
                  </Menu>
                )}

                

                <IconButton
                  icon={<SearchIcon w={4} h={4} />}
                  variant="ghost"
                  color={iconColor}
                  size="sm"
                  aria-label="Search"
                  onClick={() => setShowSearch(!showSearch)}
                  cursor="pointer"
                />

                {user && (user.role === 'admin' || user.role === 'sub-admin') && (
                  <IconButton
                    icon={<FaCog />}
                    variant="ghost"
                    color={darkRed}
                    size="sm"
                    aria-label="Admin Panel"
                    onClick={() => navigate(user.role === 'admin' ? '/admin' : '/sub-admin')}
                    cursor="pointer"
                    _hover={{ bg: 'rgba(98, 4, 4, 0.1)' }}
                  />
                )}
                <IconButton
                  icon={<HamburgerIcon w={6} h={6} />}
                  variant="ghost"
                  color={iconColor}
                  onClick={onOpen}
                  aria-label="Open Menu"
                  cursor="pointer"
                />
              </HStack>
            </Flex>
          </Flex>
        </Container>

        {/* Universal Search Overlay - Premium UI */}
        <AnimatePresence>
          {showSearch && (
            <MotionBox
              initial={{ opacity: 0, y: -20, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -20, scale: 0.98 }}
              position="absolute"
              top="100%"
              left="0"
              right="0"
              zIndex={100}
              bg="rgba(255, 255, 255, 0.9)"
              backdropFilter="blur(20px)"
              borderBottom={`1px solid ${gold}40`}
              boxShadow="0 25px 50px -12px rgba(0, 0, 0, 0.25)"
              py={8}
            >
              <Container maxW="4xl">
                <form onSubmit={handleSearch}>
                  <VStack spacing={4}>
                    <InputGroup size="lg">
                      <InputLeftElement pointerEvents="none" h="full">
                        <SearchIcon color={darkRed} w={5} h={5} />
                      </InputLeftElement>
                      <Input
                        placeholder="Search for events, history, news, or gallery..."
                        bg="white"
                        h="65px"
                        pl="55px"
                        borderRadius="20px"
                        borderColor={`${darkRed}20`}
                        fontSize="lg"
                        _focus={{
                          borderColor: gold,
                          boxShadow: `0 0 0 1px ${gold}`,
                          bg: "white"
                        }}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        autoFocus
                      />
                      <InputRightElement h="full" pr={2}>
                        <Button
                          type="submit"
                          bg={darkRed}
                          color="white"
                          h="45px"
                          px={8}
                          borderRadius="15px"
                          _hover={{ bg: '#4a0303', transform: 'scale(1.02)' }}
                          _active={{ transform: 'scale(0.98)' }}
                          cursor="pointer"
                          transition="all 0.2s"
                        >
                          {t('common.search') || "Search"}
                        </Button>
                      </InputRightElement>
                    </InputGroup>
                    <HStack spacing={4} alignSelf="start" px={2}>
                      <Text fontSize="xs" color="gray.500" fontWeight="700">SUGGESTIONS:</Text>
                      <HStack spacing={2}>
                        {['Events', 'History', 'Live TV', 'News'].map((s) => (
                          <Badge
                            key={s}
                            variant="subtle"
                            colorScheme="yellow"
                            cursor="pointer"
                            px={3}
                            py={1}
                            borderRadius="full"
                            onClick={() => {
                              setSearchQuery(s);
                              navigate(`/search?q=${encodeURIComponent(s)}`);
                              setShowSearch(false);
                              setSearchQuery('');
                            }}
                            _hover={{ bg: gold, color: darkRed }}
                          >
                            {s}
                          </Badge>
                        ))}
                      </HStack>
                    </HStack>
                  </VStack>
                </form>
              </Container>
            </MotionBox>
          )}
        </AnimatePresence>
      </Box>

      {/* Mobile Drawer */}
      <Drawer isOpen={isOpen} placement="right" onClose={onClose}>
        <DrawerOverlay />
        <DrawerContent bg={drawerBg}>
          <DrawerCloseButton color={headerText} />
          <DrawerHeader borderBottomWidth="1px" fontFamily="'Poppins', sans-serif" fontWeight="800" bg={headerBg} color={headerText} display="flex" alignItems="center" gap={3}>
            <CircularLogo size={35} />
            <Text fontSize="md">{templeAssets.templeNameShort}</Text>
          </DrawerHeader>
          <DrawerBody color={drawerBodyColor} overflowY="auto" pb={12}>
            <VStack spacing={2} align="stretch" mt={4}>
              {navItems.map((item) => (
                item.onClick ? (
                  <Button
                    key={`mobile-${item.name}`}
                    onClick={item.onClick}
                    variant="ghost"
                    w="full"
                    justifyContent="flex-start"
                    leftIcon={<Icon as={item.icon || FaHome} />}
                    color={textColor}
                    fontWeight="500"
                    bg="transparent"
                    _hover={{ bg: 'gray.100' }}
                    fontFamily="Roboto, sans-serif"
                    cursor="pointer"
                    size="md"
                    borderRadius="lg"
                  >
                    {item.name}
                  </Button>
                ) : item.isComingSoon ? (
                  <Button
                    key={`mobile-${item.name}-${item.path}`}
                    onClick={(e) => { e.preventDefault(); onClose(); onComingSoonOpen(); }}
                    variant="ghost"
                    w="full"
                    justifyContent="flex-start"
                    leftIcon={<Icon as={item.icon || FaHome} />}
                    color={isActive(item.path) ? darkRed : textColor}
                    fontWeight={isActive(item.path) ? '600' : '500'}
                    bg={isActive(item.path) ? `${gold}30` : 'transparent'}
                    _hover={{ bg: 'gray.100' }}
                    fontFamily="Roboto, sans-serif"
                    cursor="pointer"
                    size="md"
                    borderRadius="lg"
                  >
                    {item.name}
                  </Button>
                ) : (
                  <Link key={`mobile-${item.name}-${item.path}`} to={item.path} onClick={onClose} style={{ width: '100%' }}>
                    <Button
                      variant="ghost"
                      w="full"
                      justifyContent="flex-start"
                      leftIcon={<Icon as={item.icon || FaHome} />}
                      color={isActive(item.path) ? darkRed : textColor}
                      fontWeight={isActive(item.path) ? '600' : '500'}
                      bg={isActive(item.path) ? `${gold}30` : 'transparent'}
                      _hover={{ bg: 'gray.100' }}
                      fontFamily="Roboto, sans-serif"
                      cursor="pointer"
                      size="md"
                      borderRadius="lg"
                    >
                      {item.name}
                    </Button>
                  </Link>
                )
              ))}
              {user && (
                <>
                  <Divider my={2} />
                  <Box px={4} py={2} bg="gray.50" borderRadius="lg" mb={2}>
                    <Text fontWeight="bold" fontSize="sm" color={textColor}>{user.name}</Text>
                    <Text fontSize="xs" color="gray.600">{user.email}</Text>
                    {user.phone && <Text fontSize="xs" color="gray.600">{user.phone}</Text>}
                  </Box>
                  <Button
                    variant="ghost"
                    w="full"
                    justifyContent="flex-start"
                    leftIcon={<Icon as={FaUserCircle} />}
                    onClick={() => {
                      navigate('/profile');
                      onClose();
                    }}
                    fontFamily="Roboto, sans-serif"
                    cursor="pointer"
                    size="md"
                    borderRadius="lg"
                  >
                    {t('common.profile') || 'My Profile'}
                  </Button>
                  <Button
                    variant="ghost"
                    w="full"
                    justifyContent="flex-start"
                    leftIcon={<Icon as={FaUser} />}
                    onClick={() => {
                      navigate('/dashboard');
                      onClose();
                    }}
                    fontFamily="Roboto, sans-serif"
                    cursor="pointer"
                    size="md"
                    borderRadius="lg"
                  >
                    {t('common.dashboard')}
                  </Button>
                  {['admin', 'sub-admin'].includes(user.role?.toLowerCase()) && (
                    <>
                      <Button
                        variant="ghost"
                        w="full"
                        justifyContent="flex-start"
                        leftIcon={<Icon as={FaCog} />}
                        onClick={() => {
                          navigate(user.role?.toLowerCase() === 'admin' ? '/admin' : '/sub-admin');
                          onClose();
                        }}
                        fontFamily="Roboto, sans-serif"
                        cursor="pointer"
                        size="md"
                        borderRadius="lg"
                      >
                        {user.role?.toLowerCase() === 'admin' ? t('header.adminPanel') : t('header.subAdminPanel')}
                      </Button>
                      <Button
                        variant="ghost"
                        w="full"
                        justifyContent="flex-start"
                        leftIcon={<Icon as={FaBell} color="red.500" />}
                        onClick={() => {
                          navigate(user.role?.toLowerCase() === 'admin' ? '/admin?openNotifs=true' : '/sub-admin?openNotifs=true');
                          onClose();
                        }}
                        fontFamily="Roboto, sans-serif"
                        cursor="pointer"
                        size="md"
                        borderRadius="lg"
                      >
                        Admin Notifications
                      </Button>
                    </>
                  )}
                  <Divider my={2} />
                  <Button
                    variant="ghost"
                    w="full"
                    justifyContent="flex-start"
                    leftIcon={<Icon as={FaSignOutAlt} />}
                    onClick={() => {
                      handleLogout();
                      onClose();
                    }}
                    color="red.500"
                    _hover={{ bg: 'red.50' }}
                    fontFamily="Roboto, sans-serif"
                    cursor="pointer"
                    size="md"
                    borderRadius="lg"
                  >
                    {t('common.logout')}
                  </Button>
                </>
              )}
              {!user && (
                <>
                  <Divider my={2} />
                  <Button
                    variant="solid"
                    bg={darkRed}
                    color={white}
                    w="full"
                    justifyContent="flex-start"
                    leftIcon={<Icon as={FaUserCircle} />}
                    onClick={() => {
                      navigate('/login');
                      onClose();
                    }}
                    _hover={{ bg: '#4a0303' }}
                    fontFamily="Roboto, sans-serif"
                    cursor="pointer"
                    size="md"
                    borderRadius="lg"
                  >
                    {t('common.login')}
                  </Button>
                </>
              )}
            </VStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      {/* Coming Soon Glass Modal */}
      <Modal isOpen={isComingSoonOpen} onClose={onComingSoonClose} isCentered motionPreset="slideInBottom">
        <ModalOverlay backdropFilter="blur(5px)" bg="blackAlpha.600" />
        <ModalContent
          bg={'white'}
          color={darkRed}
          borderRadius="xl"
          boxShadow="2xl"
          mx={4}
        >
          <ModalHeader fontFamily="'Poppins', sans-serif" fontWeight="800" borderBottomWidth="1px" borderColor={`${gold}40`}>
            Coming Soon
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody py={6}>
            <VStack spacing={4} align="center" textAlign="center">
              <Box p={4} borderRadius="full" bg={`${gold}20`} color={darkRed}>
                <FaGopuram size="40px" />
              </Box>
              <Text fontSize="lg" fontWeight="600" fontFamily="Roboto, sans-serif">
                This feature is already developed, but we will allow it very soon!
              </Text>
              <Text fontSize="md" color="gray.500">
                These new features will be made available shortly after the main events are completed. Thank you for your patience.
              </Text>
            </VStack>
          </ModalBody>
          <ModalFooter borderTopWidth="1px" borderColor={`${gold}40`} justifyContent="center">
            <Button
              bg={darkRed}
              color="white"
              onClick={onComingSoonClose}
              _hover={{ bg: '#4a0303', transform: 'scale(1.02)' }}
              transition="all 0.2s"
              fontFamily="'Poppins', sans-serif"
            >
              OK, I Understand!
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default Header;
